import React from 'react';
import { App, Button, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { boardError, BoardOrder, priceBoardApi } from '../../services/priceBoardApi';
import { formatInt } from '../../utils/numberFormat';
import { boardPrice, fromApiPrice, STATUS_LABEL } from './boardMath';

interface Props {
    orders: BoardOrder[];
    loading?: boolean;
    compact?: boolean;
    accountName: (portfolioId: string) => string;
    onChanged: () => void;
}

const time = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/** Today's board orders, newest first; an open one can be cancelled. */
const OrdersPanel: React.FC<Props> = ({ orders, loading, compact, accountName, onChanged }) => {
    const { message } = App.useApp();
    const cancel = async (o: BoardOrder) => {
        try {
            await priceBoardApi.cancel(o.id);
            message.success('Cancel sent');
            onChanged();
        } catch (e) {
            message.error(boardError(e, 'Cancel not sent'));
        }
    };

    const columns: ColumnsType<BoardOrder> = [
        { title: 'Time', dataIndex: 'createdAt', width: 80, render: time },
        ...(compact ? [] : [{ title: 'Account', key: 'acct', width: 150, ellipsis: true, render: (_: unknown, o: BoardOrder) => accountName(o.portfolioId) }]),
        { title: 'Symbol', dataIndex: 'symbol', width: 100, render: (s: string) => <strong>{s}</strong> },
        {
            title: 'Side',
            dataIndex: 'side',
            width: 56,
            render: (s: string) => <span style={{ color: s === 'BUY' ? '#0ECB81' : '#F6465D', fontWeight: 600 }}>{s === 'BUY' ? 'Buy' : 'Sell'}</span>,
        },
        ...(compact ? [] : [{ title: 'Type', dataIndex: 'orderType', width: 64, render: (t: string) => (t === 'MARKET' ? 'Market' : 'Limit') }]),
        { title: 'Qty', dataIndex: 'quantity', width: 80, align: 'right', render: (v: number) => formatInt(v) },
        {
            title: 'Price',
            key: 'price',
            width: 90,
            align: 'right',
            render: (_: unknown, o) => boardPrice(o.symbol, fromApiPrice(o.symbol, o.price)),
        },
        {
            title: 'Matched',
            key: 'matched',
            width: 150,
            align: 'right',
            render: (_: unknown, o) =>
                o.matchedQuantity > 0
                    ? `${formatInt(o.matchedQuantity)} @ ${boardPrice(o.symbol, o.avgPrice == null ? null : fromApiPrice(o.symbol, o.avgPrice))}`
                    : '—',
        },
        {
            title: 'Status',
            key: 'status',
            width: 110,
            render: (_: unknown, o) => {
                const s = STATUS_LABEL[o.status] ?? { label: o.status, color: 'default' };
                const tag = <Tag color={s.color}>{o.cancelStatus === 'PENDING' && o.open ? 'Cancelling' : s.label}</Tag>;
                return o.rejectText ? <Tooltip title={o.rejectText}>{tag}</Tooltip> : tag;
            },
        },
        {
            title: '',
            key: 'cancel',
            width: 70,
            render: (_: unknown, o) =>
                o.open ? (
                    <Button size="small" danger type="text" onClick={() => cancel(o)}>
                        Cancel
                    </Button>
                ) : null,
        },
    ];

    // Phone: a card per order — eight columns do not fit 360px.
    if (compact) {
        if (!orders.length) return <div style={{ color: '#848E9C', padding: '8px 0' }}>{loading ? 'Loading…' : 'No orders today'}</div>;
        return (
            <div data-testid="board-order-cards">
                {orders.map((o, i) => {
                    const s = STATUS_LABEL[o.status] ?? { label: o.status, color: 'default' };
                    const label = o.cancelStatus === 'PENDING' && o.open ? 'Cancelling' : s.label;
                    const tag = <Tag color={s.color} style={{ marginInlineEnd: 0 }}>{label}</Tag>;
                    return (
                        <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: i ? '1px solid #2B3139' : undefined }}>
                            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                                <div>
                                    <span style={{ color: o.side === 'BUY' ? '#0ECB81' : '#F6465D', fontWeight: 600 }}>{o.side === 'BUY' ? 'Buy' : 'Sell'}</span>{' '}
                                    <strong>{o.symbol}</strong> {formatInt(o.quantity)} @{' '}
                                    {o.orderType === 'MARKET' ? 'Market' : boardPrice(o.symbol, fromApiPrice(o.symbol, o.price))}
                                </div>
                                <div style={{ color: '#848E9C', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {time(o.createdAt)} · {accountName(o.portfolioId)}
                                    {o.matchedQuantity > 0 && ` · matched ${formatInt(o.matchedQuantity)} @ ${boardPrice(o.symbol, o.avgPrice == null ? null : fromApiPrice(o.symbol, o.avgPrice))}`}
                                </div>
                                {o.rejectText && <div style={{ color: '#F6465D', fontSize: 11 }}>{o.rejectText}</div>}
                            </div>
                            {tag}
                            {o.open && (
                                <Button size="small" danger onClick={() => cancel(o)}>
                                    Cancel
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <Table<BoardOrder>
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={orders}
            columns={columns}
            pagination={false}
            tableLayout="fixed"
            className="price-board-table"
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No orders today' }}
        />
    );
};

export default OrdersPanel;
